import { useStandingOffer } from "@/api";
import NumerousLogo from "@/components/logo/NumerousLogo";
import { TypographyBase } from "@/components/typography/Base";
import { TypographyH3 } from "@/components/typography/H3";
import { TypographyH4 } from "@/components/typography/H4";
import { TypographySM } from "@/components/typography/SM";
import Alert from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import FullScreenLoading from "@/components/ui/fullScreenLoading";
import { InfoBox } from "@/components/ui/infoBox";
import { Label } from "@/components/ui/label";
import { AppCard } from "@/features/organization-apps";
import { StandingOfferStatus } from "@/gql/__generated__/graphql";
import Page404 from "@/routes/-components/-page404";
import { useAuth0 } from "@auth0/auth0-react";
import { Link, useLocation } from "@tanstack/react-router";
import { BookMarked, ExternalLink, Mail } from "lucide-react";
import { useHandleStandingOfferAccept } from "../hooks/handleStandingOfferAccept";
import OrganizationSection from "./OrganizationSection";

interface Props {
  standingOfferId: string;
}

const StandingOffer = ({ standingOfferId }: Props) => {
  const { isAuthenticated } = useAuth0();
  const { data, error, loading } = useStandingOffer(standingOfferId);
  const { href } = useLocation();

  const { handleSubmit, loading: loadingSubmit } =
    useHandleStandingOfferAccept(standingOfferId);

  if (loading) return <FullScreenLoading message="Loading offer" />;

  if (error)
    return (
      <div className="w-full h-screen flex flex-col items-center justify-center">
        <div className="w-fit mx-auto">
          <Alert
            variant="destructive"
            className="w-fit"
            title="Unable to fetch offer"
            description="An error occurred while fetching the offer. Please try again later."
          />
        </div>
      </div>
    );

  if (
    !data ||
    data.standingOffer.__typename === "SubscriptionOfferNotFound" ||
    data.standingOffer.status === StandingOfferStatus.Cancelled
  )
    return <Page404 />;

  return (
    <div className="grid grid-cols-2 gap-x-20 gap-y-16 min-h-full place-content-center mx-16">
      <form onSubmit={handleSubmit}>
        <TypographyH3>Review offer</TypographyH3>
        <TypographySM className="dark:text-brand-gray-300">
          Details about the offer.
        </TypographySM>

        <div className="flex flex-col gap-4 mt-8">
          <TypographyBase className="py-1 border-b dark:border-brand-gray-600">
            App information
          </TypographyBase>
          <AppCard app={data.standingOffer.app} disableLink />
          <TypographyBase className="py-1 border-b dark:border-brand-gray-600 mt-4">
            Offer details
          </TypographyBase>
          <div className="grid grid-cols-[30ch_1fr] gap-y-6">
            <Label>Organization</Label>
            <TypographySM className="text-end">
              {data.standingOffer.offeringOrganization.name}
            </TypographySM>

            <Label>Price</Label>
            <TypographySM data-testid="price" className="text-end">
              {data.standingOffer.payment
                ? `$${data.standingOffer.payment.monthlyPriceUSD}`
                : "Free"}
            </TypographySM>

            {data.standingOffer.payment && (
              <>
                <Label>Trial period</Label>
                <TypographySM data-testid="trialPeriod" className="text-end">
                  {data.standingOffer.payment.trialDays
                    ? `${data.standingOffer.payment.trialDays} days`
                    : "None"}
                </TypographySM>
              </>
            )}
            {data.standingOffer.message && (
              <>
                <Label>Message</Label>
                <TypographySM className="text-end">
                  {data.standingOffer.message}
                </TypographySM>
              </>
            )}
          </div>
          {!isAuthenticated && (
            <InfoBox className="mt-4 flex justify-end items-center">
              <TypographyBase className="w-full">
                Login or sign up to subscribe to this app
              </TypographyBase>
              <Link to="/login" search={{ redirect: href }}>
                <Button className="ml-4">Login</Button>
              </Link>
              <Link to="/signup" search={{ redirect: href }}>
                <Button className="ml-2 mr-5">Sign up</Button>
              </Link>
            </InfoBox>
          )}
          {isAuthenticated && (
            <OrganizationSection
              offerId={standingOfferId}
              isPaidOffer={!!data.standingOffer.payment}
              loadingSubmit={loadingSubmit}
            />
          )}
        </div>
      </form>

      <div>
        <InfoBox className="mt-24 p-6 grid grid-cols-[35px_1fr] gap-y-3 items-center">
          <NumerousLogo type="brandmark" size="sm" />
          <TypographyH4 className="col-start-2">
            <TypographyH4>Numerous</TypographyH4>
          </TypographyH4>
          <TypographySM className="col-start-2 row-start-2 dark:text-brand-gray-400">
            Numerous is a platform dedicated to hosting and scaling web apps.
            <br />
            <br />
            Numerous is at the intersection of cloud computing, Python, and AI.
            Whether you&apos;re building internal tools for your organization,
            creating a SaaS for external customers, or launching the core
            offering of your startup, Numerous has the infrastructure to support
            your vision.
          </TypographySM>
          <div className="col-start-2 row-start-3 place-self-end">
            <a href="mailto:support@numerous.com">
              <Button variant="ghost">
                Contact support
                <Mail className="w-4 h-4 ml-2 inline" />
              </Button>
            </a>
            <a
              href="https://www.numerous.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button variant="ghost">
                numerous.com
                <ExternalLink className="w-4 h-4 ml-2 inline" />
              </Button>
            </a>
          </div>
        </InfoBox>

        <div className="rounded border dark:border-brand-gray-800 h-fit mt-8 p-6 grid grid-cols-[35px_1fr] gap-y-3 items-center">
          <BookMarked
            className="h-6 w-6 dark:text-brand-gray-600"
            strokeWidth="1.6"
          />
          <TypographyH4 className="col-start-2">Subscriptions</TypographyH4>
          <TypographySM className="col-start-2 row-start-2 dark:text-brand-gray-400">
            Subscriptions let you access apps from other organizations. Once an
            offer is accepted, members of your organization will be able to use
            the app.
            <br />
            <br />
            Subscriptions can be cancelled at any time by either your
            organization or the owner organization.
          </TypographySM>
          <a
            href="https://www.numerous.com/docs"
            className="col-start-2 row-start-3 place-self-end"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button variant="ghost">
              Read more
              <ExternalLink className="w-4 h-4 ml-2 inline" />
            </Button>
          </a>
        </div>
      </div>
    </div>
  );
};

export default StandingOffer;
